import { useState } from 'react';
import { ChevronDown, Save, Trash2 } from 'lucide-react';
import {
  AUDIO_CODECS,
  COLOR_SPACES,
  CONTAINERS,
  ENCODE_PRESETS,
  ENCODER_SPEEDS,
  PIXEL_FORMATS,
  PRESET_GROUPS,
  SCALE_MODES,
  adaptEncode,
  audioCodecSpec,
  containerSpec,
  isMasterEncode,
  normalizeEncode,
  pixelFormatsFor,
  presetEncode,
  videoCodecSpec,
  type EncodePreset,
  type EncodeSettings,
} from '@vidmakr/shared';
import { Button, Field, Input, Select, Toggle, cx } from '@/components/ui';

/**
 * Output settings like Adobe Media Encoder, shared by the Export dialog and
 * Convert media: a preset picker (built-in and saved presets) and an Advanced
 * section with every setting of the format, codec and audio.
 */

const USER_PRESETS_KEY = 'vm:encodePresets';

/** A preset saved in this browser. */
export interface UserPreset {
  id: string;
  label: string;
  encode: EncodeSettings;
  width?: number;
  height?: number;
  fps?: number;
}

function loadUserPresets(): UserPreset[] {
  try {
    const list = JSON.parse(localStorage.getItem(USER_PRESETS_KEY) ?? '[]');
    return Array.isArray(list) ? list.filter((p) => p && typeof p.id === 'string' && p.encode).map((p) => ({ ...p, encode: normalizeEncode(p.encode) })) : [];
  } catch {
    return [];
  }
}

/** Presets the person saved (localStorage), with save and delete. */
export function useUserPresets() {
  const [presets, setPresets] = useState<UserPreset[]>(loadUserPresets);
  const store = (next: UserPreset[]) => {
    setPresets(next);
    try {
      localStorage.setItem(USER_PRESETS_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };
  return {
    presets,
    save: (p: Omit<UserPreset, 'id'>) => {
      const id = `user:${Date.now().toString(36)}`;
      store([...presets.filter((x) => x.label !== p.label), { ...p, id }]);
      return id;
    },
    remove: (id: string) => store(presets.filter((p) => p.id !== id)),
  };
}

/** A built-in or saved preset by id. */
export function findPreset(id: string, user: UserPreset[]): (Pick<EncodePreset, 'width' | 'height' | 'shortSide' | 'fps' | 'loudnessLufs'> & { id: string; label: string; encode: EncodeSettings }) | null {
  const builtIn = ENCODE_PRESETS.find((p) => p.id === id);
  if (builtIn) return { ...builtIn, encode: presetEncode(builtIn) };
  return user.find((p) => p.id === id) ?? null;
}

/**
 * Preset picker. `allow` hides presets that cannot be used here (e.g. image
 * sequences for conversions); `locked` marks the ones the plan does not include.
 */
export function PresetPicker({
  value,
  onChange,
  user,
  allow = () => true,
  locked = () => false,
}: {
  value: string;
  onChange: (id: string) => void;
  user: UserPreset[];
  allow?: (e: EncodeSettings) => boolean;
  locked?: (e: EncodeSettings) => boolean;
}) {
  const shown = user.filter((p) => allow(p.encode));
  return (
    <Select className="w-full" value={value} onChange={(e) => onChange(e.target.value)} data-encode-preset>
      {value === 'custom' ? <option value="custom">Custom</option> : null}
      {shown.length ? (
        <optgroup label="My presets">
          {shown.map((p) => (
            <option key={p.id} value={p.id} disabled={locked(p.encode)}>
              {p.label}
              {locked(p.encode) ? ' — Pro' : ''}
            </option>
          ))}
        </optgroup>
      ) : null}
      {PRESET_GROUPS.map((g) => {
        const list = ENCODE_PRESETS.filter((p) => p.group === g && allow(presetEncode(p)));
        return list.length ? (
          <optgroup key={g} label={g}>
            {list.map((p) => {
              const lock = locked(presetEncode(p));
              return (
                <option key={p.id} value={p.id} disabled={lock} title={p.description}>
                  {p.label}
                  {lock ? ' — Pro' : ''}
                </option>
              );
            })}
          </optgroup>
        ) : null;
      })}
    </Select>
  );
}

/** Saves the current settings as a preset, or deletes the selected saved one. */
export function PresetActions({
  presetId,
  onSave,
  onDelete,
}: {
  presetId: string;
  onSave: (label: string) => void;
  onDelete: (id: string) => void;
}) {
  const [naming, setNaming] = useState<string | null>(null);
  if (naming !== null) {
    return (
      <form
        className="flex items-center gap-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (naming.trim()) onSave(naming.trim().slice(0, 60));
          setNaming(null);
        }}
      >
        <Input autoFocus className="h-7 w-44 text-xs" placeholder="Preset name" value={naming} onChange={(e) => setNaming(e.target.value)} data-preset-name />
        <Button size="sm" variant="primary" type="submit" disabled={!naming.trim()}>
          Save
        </Button>
        <Button size="sm" variant="ghost" type="button" onClick={() => setNaming(null)}>
          Cancel
        </Button>
      </form>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="ghost" onClick={() => setNaming('')} data-save-preset>
        <Save size={13} /> Save preset
      </Button>
      {presetId.startsWith('user:') ? (
        <Button size="icon-sm" variant="ghost" title="Delete this preset" onClick={() => onDelete(presetId)}>
          <Trash2 size={13} />
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Every setting of the output, grouped like Media Encoder: format, video and
 * audio. `containers` limits the formats offered; `gpu` notes NVENC limits;
 * `matchSource` labels the "0" choices for conversions.
 */
export function EncodeSettingsEditor({
  value,
  onChange,
  containers = CONTAINERS.map((c) => c.id),
  masterLocked = false,
  matchSource = false,
}: {
  value: EncodeSettings;
  onChange: (e: EncodeSettings) => void;
  containers?: string[];
  masterLocked?: boolean;
  matchSource?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const set = (patch: Partial<EncodeSettings>) => onChange(adaptEncode(value, patch));
  const container = containerSpec(value.container);
  const codec = videoCodecSpec(value.videoCodec);
  const audio = audioCodecSpec(value.audioCodec);
  const num = (v: string) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  return (
    <div className="rounded-lg border border-line" data-encode-advanced>
      <button className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-muted hover:text-fg" onClick={() => setOpen(!open)}>
        <span className="flex-1 text-left">Advanced settings</span>
        <ChevronDown size={14} className={cx('transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="space-y-4 border-t border-line p-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field label="Format">
              <Select className="w-full" value={value.container} onChange={(e) => set({ container: e.target.value })} data-encode-container>
                {CONTAINERS.filter((c) => containers.includes(c.id)).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </Field>
            {container?.videoCodecs.length ? (
              <Field label="Video codec">
                <Select className="w-full" value={value.videoCodec} onChange={(e) => set({ videoCodec: e.target.value })} data-encode-codec>
                  {container.videoCodecs.map((id) => {
                    const c = videoCodecSpec(id)!;
                    const lock = masterLocked && !!c.master;
                    return (
                      <option key={id} value={id} disabled={lock}>
                        {c.label}
                        {lock ? ' — Pro' : ''}
                      </option>
                    );
                  })}
                </Select>
              </Field>
            ) : null}
            {codec?.profiles ? (
              <Field label="Profile">
                <Select className="w-full" value={value.profile || codec.defaultProfile} onChange={(e) => set({ profile: e.target.value })} data-encode-profile>
                  {codec.profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {codec?.levels ? (
              <Field label="Level">
                <Select className="w-full" value={value.level} onChange={(e) => set({ level: e.target.value })}>
                  <option value="">Automatic</option>
                  {codec.levels.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {codec && pixelFormatsFor(value).length > 1 ? (
              <Field label="Bit depth / chroma">
                <Select className="w-full" value={value.pixelFormat || pixelFormatsFor(value)[0]} onChange={(e) => set({ pixelFormat: e.target.value })} data-encode-pixfmt>
                  {pixelFormatsFor(value).map((id) => (
                    <option key={id} value={id}>
                      {PIXEL_FORMATS.find((p) => p.id === id)?.label ?? id}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
          </div>

          {codec && codec.rateControls.length ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Bitrate encoding">
                <Select className="w-full" value={value.rateControl} onChange={(e) => set({ rateControl: e.target.value as EncodeSettings['rateControl'] })} data-encode-rate>
                  {codec.rateControls.map((r) => (
                    <option key={r} value={r}>
                      {r === 'crf' ? 'Constant quality' : r === 'cbr' ? 'CBR (constant bitrate)' : 'VBR (variable bitrate)'}
                    </option>
                  ))}
                </Select>
              </Field>
              {value.rateControl === 'crf' && codec.quality ? (
                <Field label={codec.quality.label} hint={codec.quality.higherIsBetter ? 'higher = better' : 'lower = better'}>
                  <Input
                    type="number"
                    min={codec.quality.min}
                    max={codec.quality.max}
                    value={value.quality}
                    onChange={(e) => onChange({ ...value, quality: num(e.target.value) })}
                    data-encode-quality
                  />
                </Field>
              ) : null}
              {value.rateControl !== 'crf' ? (
                <Field label="Target bitrate" hint="Mbps">
                  <Input
                    type="number"
                    min={0.1}
                    step={0.5}
                    value={value.bitrateKbps / 1000}
                    onChange={(e) => {
                      const bitrateKbps = Math.round(num(e.target.value) * 1000);
                      // The peak follows the target while it is still the automatic 1.5×.
                      const auto = value.maxBitrateKbps === Math.round(value.bitrateKbps * 1.5);
                      onChange({ ...value, bitrateKbps, maxBitrateKbps: auto ? Math.round(bitrateKbps * 1.5) : value.maxBitrateKbps });
                    }}
                    data-encode-bitrate
                  />
                </Field>
              ) : null}
              {value.rateControl === 'vbr' ? (
                <Field label="Maximum bitrate" hint="Mbps">
                  <Input type="number" min={0.1} step={0.5} value={value.maxBitrateKbps / 1000} onChange={(e) => onChange({ ...value, maxBitrateKbps: Math.round(num(e.target.value) * 1000) })} />
                </Field>
              ) : null}
              {codec.gop ? (
                <Field label="Keyframe every" hint="0 = auto">
                  <Input type="number" min={0} max={1000} value={value.keyframeInterval} onChange={(e) => onChange({ ...value, keyframeInterval: Math.max(0, Math.round(num(e.target.value))) })} />
                </Field>
              ) : null}
              <Field label="Encoder speed">
                <Select className="w-full" value={value.speed} onChange={(e) => set({ speed: e.target.value })}>
                  {ENCODER_SPEEDS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </Field>
              {value.rateControl === 'vbr' && codec.twoPass ? (
                <div className="flex items-end pb-2">
                  <Toggle checked={value.twoPass} onChange={(v) => onChange({ ...value, twoPass: v })} label="2-pass" />
                </div>
              ) : null}
            </div>
          ) : null}

          {codec ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Color space tag" hint={value.colorSpace === 'bt2020' ? 'tag only' : undefined}>
                <Select className="w-full" value={value.colorSpace} onChange={(e) => set({ colorSpace: e.target.value })}>
                  {COLOR_SPACES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Other aspect ratios">
                <Select className="w-full" value={value.scaleMode} onChange={(e) => set({ scaleMode: e.target.value })}>
                  {SCALE_MODES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          ) : null}

          {container?.audioCodecs.length ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Audio codec">
                <Select className="w-full" value={value.audioCodec} onChange={(e) => set({ audioCodec: e.target.value })} data-encode-audio>
                  {container.kind !== 'audio' ? <option value="">No audio</option> : null}
                  {container.audioCodecs.map((id) => (
                    <option key={id} value={id}>
                      {AUDIO_CODECS.find((a) => a.id === id)?.label ?? id}
                    </option>
                  ))}
                </Select>
              </Field>
              {audio?.bitrates.length ? (
                <Field label="Audio bitrate">
                  <Select className="w-full" value={value.audioBitrateKbps} onChange={(e) => set({ audioBitrateKbps: Number(e.target.value) })}>
                    {audio.bitrates.map((b) => (
                      <option key={b} value={b}>
                        {b} kbps
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : null}
              {audio ? (
                <Field label="Sample rate">
                  <Select className="w-full" value={value.audioSampleRate} onChange={(e) => set({ audioSampleRate: Number(e.target.value) })}>
                    {matchSource ? <option value={0}>Match source</option> : null}
                    {audio.sampleRates.filter((r) => value.container !== 'mxf' || r === 48000).map((r) => (
                      <option key={r} value={r}>
                        {r / 1000} kHz
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : null}
              {audio ? (
                <Field label="Channels">
                  <Select className="w-full" value={value.audioChannels} onChange={(e) => set({ audioChannels: Number(e.target.value) })}>
                    {matchSource ? <option value={0}>Match source</option> : null}
                    <option value={2}>Stereo</option>
                    <option value={1}>Mono</option>
                  </Select>
                </Field>
              ) : null}
            </div>
          ) : null}
          {container?.note ? <p className="text-xs text-faint">{container.note}</p> : null}
          {masterLocked && isMasterEncode(value) ? <p className="text-xs text-warn">This codec is part of the Pro plan.</p> : null}
        </div>
      ) : null}
    </div>
  );
}
